import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import {
    getCurrentPage,
    getLocation,
    removeAbsoluteUrl,
} from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SignInChangePasswordForm from "@insite/content-library/Widgets/SignIn/SignInChangePasswordForm";
import { CheckboxProps } from "@insite/mobius/Checkbox";
import { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import GenericModal from "@sonic/CustomComponents/headless/components/GenericModal/GenericModal";
import Tooltip from "@sonic/CustomComponents/headless/components/Tooltip/Tooltip";
import { Button } from "@sonic/CustomComponents/headless/elements/Button/Button";
import { ButtonPresentationProps } from "@sonic/Overrides/Components/Button";
import Checkbox from "@sonic/Overrides/Components/Checkbox";
import TextField from "@sonic/Overrides/Components/TextField";
import { tooltipText } from "@sonic/Overrides/Widgets/CreateAccount/CreateAccount";
import SignInResetPasswordForm from "@sonic/Overrides/Widgets/SignIn/SignInResetPasswordForm";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    changePasswordInstructions = "changePasswordInstructions",
    fullWidthButton = "fullWidthButton",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.changePasswordInstructions]: string;
        [fields.fullWidthButton]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ returnUrl?: string; externalError?: string }>(location.search);
    const pageType = getCurrentPage(state).type;
    const isSignInPage = pageType === "SignInPage";
    const isConfirmationPage = pageType === "OrderConfirmationPage" || pageType === "OnePageCheckoutConfirmationPage";
    const returnUrl = isSignInPage
        ? removeAbsoluteUrl(parsedQuery.returnUrl)
        : encodeURIComponent(`${location.pathname}${location.search}`);

    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        returnUrl,
        externalError: parsedQuery.externalError,
        isConfirmationPage,
        homePageUrl: getHomePageUrl(state),
    };
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

const mapDispatchToProps = {
    signIn,
};

export interface SignInExistingAccountStyles {
    form?: InjectableCss;
    rememberMeCheckbox?: CheckboxProps;
    rememberMeCheckboxGroup?: CheckboxGroupComponentProps;
    rememberMeWrapper?: InjectableCss;
    userNameTextField?: TextFieldProps;
    signInGridContainer?: GridContainerProps;
    icon?: IconProps;
    passwordTextField?: TextFieldProps;
    signInButton?: ButtonPresentationProps;
    signInButtonGridItem?: GridItemProps;
    forgotPasswordGridItem?: GridItemProps;
    rememberMeGridItem?: GridItemProps;
    passwordGridItem?: GridItemProps;
    userNameGridItem?: GridItemProps;
    signInExistingAccountTitle?: TypographyProps;
    forgotPasswordLink?: LinkPresentationProps;
    resetPasswordModal?: ModalPresentationProps;
    changePasswordModal?: ModalPresentationProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
}

export const signInExistingAccountStyles: SignInExistingAccountStyles = {
    icon: {
        css: css`
            padding-top: 50px;
        `,
    },
    rememberMeWrapper: {},
    signInExistingAccountTitle: {
        variant: "h4",
    },
    userNameGridItem: {
        width: 12,
    },
    userNameTextField: {},
    passwordGridItem: {
        width: 12,
        css: css`
            position: relative;

            .info {
                svg {
                    color: #bdbcbc;

                    &:hover {
                        color: #ededed;
                    }
                }
            }
        `,
    },
    signInButtonGridItem: {
        width: 12,
        css: css`
            margin-top: 24px;
            justify-content: flex-end;
        `,
    },
    rememberMeGridItem: {
        width: 6,
        css: css`
            align-items: center;
        `,
    },
    forgotPasswordGridItem: {
        width: 6,
        css: css`
            margin-top: 20px;
            justify-content: flex-end;
            align-items: center;
        `,
    },
    forgotPasswordLink: {
        typographyProps: {
            size: 13,
        },
    },
    resetPasswordModal: {
        sizeVariant: "small",
    },
    changePasswordModal: {
        sizeVariant: "small",
    },
    signInButton: {
        buttonType: "solid",
        variant: "primary",
        shape: "pill",
        typographyProps: {
            color: "#ffffff",
            fontFamily: "Pancetta W05 Bold Italic, sans-serif",
        },
        css: css`
            text-transform: uppercase;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            margin-top: 25px;
            min-width: 155px;
        `,
    },
    passwordRequirementsGridContainer: {
        gap: 5,
    },
    passwordRequirementsGridItem: {
        width: 12,
    },
};

/**
 * @deprecated Use signInExistingAccountStyles instead.
 */
export const signInExistingAccount = signInExistingAccountStyles;
const styles = signInExistingAccountStyles;
const StyledForm = getStyledWrapper("form");

export const StyledUl = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 22px;
    padding-left: 17px;
    margin-left: -32px;

    li {
        position: relative;
        display: flex;
        align-items: flex-start;

        &::before {
            display: block;
            background-color: #000;
            content: "";
            padding: 2.5px;
            margin-top: 6.5px;
            margin-right: 12px;
        }
    }
`;

const SignInExistingAccount = ({
    signIn,
    fields,
    accountSettings,
    returnUrl,
    externalError,
    isConfirmationPage,
    homePageUrl,
    theme,
}: Props) => {
    const [rememberMe, setRememberMe] = useState(false);
    const [keepMeSignedIn, setKeepMeSignedIn] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [isSignInClicked, setIsSignInClicked] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(externalError || "");

    const showRememberMe = accountSettings.rememberMe;
    const showKeepMeSignedIn = accountSettings.keepMeSignedIn;

    const signInHandler = (newPassword?: string) => {
        const pass = newPassword || password;
        setIsSignInClicked(true);
        if (!userName || !pass) {
            return;
        }

        if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
            return;
        }

        setErrorMessage("");

        signIn({
            userName,
            password: pass,
            rememberMe,
            keepMeSignedIn,
            returnUrl,
            skipRedirect: theme.signIn?.useOverlay && !isConfirmationPage,
            onError: (error, statusCode) => {
                if (statusCode === 422) {
                    setIsChangePasswordModalOpen(true);
                    return;
                }

                setErrorMessage(error);
            },
        });
    };

    const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        signInHandler();
    };

    const onResetPasswordClose = () => {
        setIsResetPasswordModalOpen(false);
    };

    const onChangePasswordClose = () => {
        setIsChangePasswordModalOpen(false);
    };

    const onPasswordChanged = (newPassword: string) => {
        setPassword(newPassword);
        setIsChangePasswordModalOpen(false);
        signInHandler(newPassword);
    };

    const validateUserName = () => {
        if (isSignInClicked) {
            if (userName) {
                if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
                    return siteMessage("SignInInfo_Email_Validation");
                }
            } else {
                return accountSettings.useEmailAsUserName
                    ? siteMessage("SignInInfo_Email_Required")
                    : siteMessage("SignInInfo_UserName_Required");
            }
        }

        return "";
    };

    const userErrorMessage = validateUserName();
    const passwordErrorMessage =
        !password && isSignInClicked ? siteMessage("SignInInfo_Password_Required") : errorMessage || "";

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
            setIsResetPasswordModalOpen(false);
        }
    };

    React.useEffect(() => {
        if (isResetPasswordModalOpen) {
            document.addEventListener("keydown", handleKeyDown);
            return () => {
                document.removeEventListener("keydown", handleKeyDown);
            };
        }
    }, [isResetPasswordModalOpen]);

    return (
        <>
            {/* <Typography {...styles.signInExistingAccountTitle}>{translate("Already have an account?")}</Typography> */}
            <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
                <GridContainer gap={0} style={{ maxWidth: 350, margin: "0 auto 0 0" }} {...styles.signInGridContainer}>
                    <GridItem {...styles.userNameGridItem}>
                        <TextField
                            id="userName"
                            {...styles.userNameTextField}
                            label={accountSettings.useEmailAsUserName ? translate("Email") : translate("User Name")}
                            onChange={e => setUserName(e.currentTarget.value)}
                            value={userName}
                            required={true}
                            backgroundColor="#ededed"
                            error={userErrorMessage}
                            autoComplete="username"
                            data-test-selector="signIn_userName"
                        />
                    </GridItem>
                    <GridItem {...styles.passwordGridItem}>
                        <TextField
                            id="password"
                            {...styles.passwordTextField}
                            required={true}
                            label={translate("Password")}
                            onChange={e => setPassword(e.currentTarget.value)}
                            value={password}
                            error={passwordErrorMessage}
                            icon={{ width: 24, height: 24 }}
                            iconName={showPassword ? "hashed" : "dehashed"}
                            backgroundColor="#ededed"
                            iconClickableProps={{
                                onClick: () => {
                                    setShowPassword(!showPassword);
                                },
                                type: "button",
                            }}
                            clickableText={password}
                            autoComplete="current-password"
                            data-test-selector="signIn_password"
                            type={showPassword ? "text" : "password"}
                        />
                        <Tooltip
                            cssOverrides={{
                                tooltipContainer: css`
                                    bottom: -219%;
                                    left: -226px;
                                    margin-left: 0;

                                    &::after {
                                        top: 59%;
                                        left: calc(100% + 6px);
                                        border-color: transparent transparent transparent
                                            ${getColor("common.backgroundContrast")};
                                    }
                                `,
                                tooltipWrapper: css`
                                    position: absolute;
                                    right: -28px;
                                    bottom: 36px;
                                    transform: translateY(50%);
                                `,
                            }}
                            text={tooltipText}
                        ></Tooltip>
                    </GridItem>
                    <GridItem {...styles.rememberMeGridItem}>
                        {showRememberMe && (
                            <StyledWrapper {...styles.rememberMeCheckboxGroup}>
                                <Checkbox
                                    uid="rememberMe"
                                    color="primary"
                                    {...styles.rememberMeCheckbox}
                                    checked={rememberMe}
                                    onChange={(e, value) => setRememberMe(value)}
                                >
                                    {translate("Remember Me")}
                                </Checkbox>
                            </StyledWrapper>
                        )}
                    </GridItem>
                    <GridItem {...styles.forgotPasswordGridItem}>
                        <SignInResetPasswordForm
                            isResetPasswordModalOpen={isResetPasswordModalOpen}
                            setIsOpen={setIsResetPasswordModalOpen}
                            onClose={() => onResetPasswordClose()}
                        />
                    </GridItem>
                    <GridItem {...styles.signInButtonGridItem}>
                        <Button
                            type="primaryRoundButton"
                            title={translate("Sign In")}
                            customStyles={css`
                                text-transform: uppercase;
                                display: flex;
                                justify-content: center;
                                align-items: center;
                                width: 100% !important;
                            `}
                            icon={{ iconName: "arrowRight", iconWidth: 12, iconHeight: 12 }}
                            buttonType="submit"
                            testSelector="signIn_submit"
                        />
                        {isChangePasswordModalOpen && (
                            <GenericModal
                                title={translate("Change Password")}
                                {...styles.changePasswordModal}
                                setIsOpen={onChangePasswordClose}
                            >
                                <SignInChangePasswordForm
                                    enteredUserName={userName}
                                    enteredPassword={password}
                                    instructionsText={fields.changePasswordInstructions}
                                    onPasswordChanged={onPasswordChanged}
                                />
                            </GenericModal>
                        )}
                    </GridItem>
                </GridContainer>
                <Link
                    {...styles.forgotPasswordLink}
                    type="button"
                    onClick={() => setIsResetPasswordModalOpen(true)}
                    data-test-selector="signIn_forgotPassword"
                >
                    {translate("Forgot Password")}
                </Link>
            </StyledForm>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(SignInExistingAccount)),
    definition: {
        allowedContexts: ["SignInPage", "SignInOverlay"],
        group: "Sign In",
        icon: "LogIn",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.changePasswordInstructions,
                editorTemplate: "RichTextField",
                displayName: "Change Password Instructions",
                defaultValue: "A password change is required for your account before you may continue.",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                sortOrder: 0,
            },
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
